import { c as _c } from "react/compiler-runtime";
import Badge from '@precis-digital/kurama/Badge';
import Chip from '@precis-digital/kurama/Chip';
import ComparisonChip from '@precis-digital/kurama/ComparisonChip';
import { styled } from '@precis-digital/kurama/utils';
import { type ThemeColor } from '@precis-digital/kurama/theme';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { type ReactElement } from 'react';
import { formatToPercentWithOptions } from 'shared/numberFormat';
import { useChartCardDefaults } from '../../hooks/useChartCardDefaults';
interface LegendItemProps {
  color?: ThemeColor;
  label: string;
  /** Explains an otherwise opaque label, e.g. the '-' shown for a null dimension value. */
  labelTooltip?: string;
  value?: number | string;
  percentage?: number;
  changePercentage?: number;
  isLastEntry: boolean;
  isDimmed?: boolean;
  rowRef: (el: HTMLTableRowElement | null | undefined) => void;
}
const PercentageChip = t0 => {
  const $ = _c(6);
  const {
    percentage,
    isDimmed: t1
  } = t0;
  const isDimmed = t1 === undefined ? false : t1;
  const defaults = useChartCardDefaults();
  let t2;
  if ($[0] !== defaults.axis.format.percentage || $[1] !== percentage) {
    t2 = formatToPercentWithOptions(percentage, defaults.axis.format.percentage);
    $[0] = defaults.axis.format.percentage;
    $[1] = percentage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const value = t2;
  let t3;
  if ($[3] !== isDimmed || $[4] !== value) {
    t3 = <Chip label={value} disabled={isDimmed} />;
    $[3] = isDimmed;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const LegendItem = t0 => {
  const $ = _c(37);
  const {
    color,
    label,
    labelTooltip,
    value,
    percentage,
    changePercentage,
    isLastEntry,
    isDimmed: t1,
    rowRef
  } = t0;
  const isDimmed = t1 === undefined ? false : t1;
  const defaults = useChartCardDefaults();
  const textColor = isDimmed ? "disabled" : "primary";
  let rows;
  if ($[0] !== changePercentage || $[1] !== color || $[2] !== defaults || $[3] !== isDimmed || $[4] !== isLastEntry || $[5] !== label || $[6] !== labelTooltip || $[7] !== percentage || $[8] !== rowRef || $[9] !== textColor || $[10] !== value) {
    rows = [];
    let t2;
    if ($[12] !== label || $[13] !== textColor) {
      t2 = <StyledLabel variant="body2" color={textColor}>{label}</StyledLabel>;
      $[12] = label;
      $[13] = textColor;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    const labelElement = t2;
    let t3;
    if ($[15] !== color) {
      t3 = color != null && <StyledCellLeft><BadgeCellContent><Badge color={color} variant="lighter" /></BadgeCellContent></StyledCellLeft>;
      $[15] = color;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== labelElement || $[18] !== labelTooltip) {
      t4 = <StyledCellLabel>{labelTooltip == null ? labelElement : <Tooltip kind="singleline" title={labelTooltip}>{labelElement}</Tooltip>}</StyledCellLabel>;
      $[17] = labelElement;
      $[18] = labelTooltip;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    let t5;
    if ($[20] !== textColor || $[21] !== value) {
      t5 = value != null && <StyledCellRight><Typography variant="body2" color={textColor}>{value}</Typography></StyledCellRight>;
      $[20] = textColor;
      $[21] = value;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== isDimmed || $[24] !== percentage) {
      t6 = percentage != null && <StyledCellRight><PercentageChip percentage={percentage} isDimmed={isDimmed} /></StyledCellRight>;
      $[23] = isDimmed;
      $[24] = percentage;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== changePercentage || $[27] !== defaults) {
      t7 = changePercentage != null && <StyledCellRight><ComparisonChip value={changePercentage} positiveIsGood={true} labelFormatter={v => formatToPercentWithOptions(v, defaults.axis.format.percentage)} /></StyledCellRight>;
      $[26] = changePercentage;
      $[27] = defaults;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== rowRef || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
      t8 = <StyledRow ref={rowRef}>{t3}{t4}{t5}{t6}{t7}</StyledRow>;
      $[29] = rowRef;
      $[30] = t3;
      $[31] = t4;
      $[32] = t5;
      $[33] = t6;
      $[34] = t7;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    rows.push(t8);
    if (!isLastEntry) {
      let t9;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <StyledEmptyRow />;
        $[36] = t9;
      } else {
        t9 = $[36];
      }
      rows.push(t9);
    }
    $[0] = changePercentage;
    $[1] = color;
    $[2] = defaults;
    $[3] = isDimmed;
    $[4] = isLastEntry;
    $[5] = label;
    $[6] = labelTooltip;
    $[7] = percentage;
    $[8] = rowRef;
    $[9] = textColor;
    $[10] = value;
    $[11] = rows;
  } else {
    rows = $[11];
  }
  return rows;
};
const StyledEmptyRow = styled('tr')(({
  theme
}) => ({
  width: '100%',
  height: theme.spacing(3),
  padding: 0,
  border: 'none'
}));
const StyledRow = styled('tr')(({
  theme
}) => ({
  width: '100%',
  height: theme.spacing(8),
  padding: 0,
  borderBottom: `1px solid ${theme.tokens.background.tonal.soft.default}`,
  '&:last-child': {
    borderBottom: 'none'
  }
}));
const StyledCellLeft = styled('td')(() => ({
  padding: '0',
  width: 'fit-content'
}));
const BadgeCellContent = styled('div')(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  minHeight: theme.spacing(8)
}));
const StyledCellRight = styled('td')(({
  theme
}) => ({
  padding: `0 ${theme.spacing(0)}`,
  textAlign: 'right',
  verticalAlign: 'middle',
  width: 'fit-content'
}));
const StyledCellLabel = styled('td')(({
  theme
}) => ({
  padding: `0 ${theme.spacing(0)}`,
  textAlign: 'left',
  verticalAlign: 'middle',
  width: '100%'
}));
const StyledLabel = styled(Typography)(() => ({
  display: 'block',
  width: 'fit-content',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));
export default LegendItem;